/**
 * Asset Service
 * 
 * API client for asset management operations (upload, list, delete)
 */

import { getApiBaseUrl } from '../config/environment';

const API_BASE = getApiBaseUrl();

// Asset types
export type AssetCategory = 'thumbnails' | 'references' | 'icons' | 'general';

export interface Asset {
  assetId: string;
  tenantId: string;
  filename: string;
  displayName: string;
  category: AssetCategory;
  mimeType: string;
  size: number;
  url: string;
  uploadedBy: string;
  uploadedAt: string;
  description?: string;
  tags?: string[];
}

export interface UploadAssetRequest {
  dataUrl: string;
  filename: string;
  displayName?: string;
  category?: AssetCategory;
  description?: string;
  tags?: string[];
}

export interface ListAssetsResponse {
  assets: Asset[];
  total: number;
}

export interface UploadAssetResponse {
  asset: Asset;
  message: string;
}

// Cached blob base URL
let cachedBlobBaseUrl: string | null = null;

export class AssetService {
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  /**
   * List assets for a tenant
   */
  async listAssets(
    tenantId: string, 
    options?: { category?: AssetCategory; search?: string; limit?: number }
  ): Promise<ListAssetsResponse> {
    const queryParams = new URLSearchParams();
    if (options?.category) queryParams.append('category', options.category);
    if (options?.search) queryParams.append('search', options.search);
    if (options?.limit) queryParams.append('limit', options.limit.toString());

    const url = `${API_BASE}/api/tenants/${tenantId}/assets?${queryParams.toString()}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to list assets' }));
      throw new Error(error.message || 'Failed to list assets');
    }

    return response.json();
  }

  /**
   * Upload a new asset
   */
  async uploadAsset(tenantId: string, request: UploadAssetRequest): Promise<UploadAssetResponse> {
    const url = `${API_BASE}/api/tenants/${tenantId}/assets`;
    
    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to upload asset' }));
      throw new Error(error.message || 'Failed to upload asset');
    }

    return response.json();
  }

  /**
   * Get a single asset
   */
  async getAsset(assetId: string, tenantId?: string): Promise<Asset> {
    const queryParams = tenantId ? `?tenantId=${tenantId}` : '';
    const url = `${API_BASE}/api/assets/${assetId}${queryParams}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to get asset' }));
      throw new Error(error.message || 'Failed to get asset');
    }

    const data = await response.json();
    return data.asset;
  }

  /**
   * Update asset metadata
   */
  async updateAsset(
    assetId: string, 
    updates: { displayName?: string; description?: string; tags?: string[] },
    tenantId?: string
  ): Promise<Asset> {
    const queryParams = tenantId ? `?tenantId=${tenantId}` : '';
    const url = `${API_BASE}/api/assets/${assetId}${queryParams}`;
    
    const response = await fetch(url, {
      method: 'PATCH',
      headers: this.getHeaders(),
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to update asset' }));
      throw new Error(error.message || 'Failed to update asset');
    }

    const data = await response.json();
    return data.asset;
  }

  /**
   * Delete an asset
   */
  async deleteAsset(assetId: string, tenantId?: string): Promise<void> {
    const queryParams = tenantId ? `?tenantId=${tenantId}` : '';
    const url = `${API_BASE}/api/assets/${assetId}${queryParams}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to delete asset' }));
      throw new Error(error.message || 'Failed to delete asset');
    }
  }

  /**
   * Get blob base URL for resolving asset:// references
   */
  async getBlobBaseUrl(): Promise<string> {
    // Return cached value if available
    if (cachedBlobBaseUrl) {
      return cachedBlobBaseUrl;
    }

    const url = `${API_BASE}/api/asset-config/resolve-url`;

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      console.error('Failed to get blob base URL, using fallback');
      return '';
    }

    const data = await response.json();
    cachedBlobBaseUrl = data.baseUrl;
    return data.baseUrl;
  }

  /**
   * Resolve an asset:// URL to a full blob URL
   */
  async resolveAssetUrl(assetUrl: string, tenantId: string): Promise<string> {
    if (!assetUrl) return '';

    // If already a full URL, return as-is
    if (assetUrl.startsWith('http://') || assetUrl.startsWith('https://')) {
      return assetUrl;
    }

    // Parse asset:// URL
    if (assetUrl.startsWith('asset://')) {
      const baseUrl = await this.getBlobBaseUrl();
      const assetPath = assetUrl.replace('asset://', '');
      return `${baseUrl}/tenant-assets/${tenantId}/${assetPath}`;
    }

    return assetUrl;
  }

  /**
   * Convert a File to a data URL for upload
   */
  static async fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}

// Export singleton instance
export const assetService = new AssetService();
